import type { ComputedRef } from 'vue'
import type { ColorEnum } from '../../enums'
import type { KpisComposable } from '../use-kpis'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { computed, getCurrentInstance, watch } from 'vue'
import { KpiEnum } from '../../enums/kpi.enum'
import { capitalizeAllWords } from '../../filters/capitalize-sentence.filter'
import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'
import { useContextQuery } from '../use-context-query/index'
import { useKpis } from '../use-kpis'
import { useReloadDistDataWhenFiltersChange } from '../use-reload-dist-data-when-filters-change'
import { useStatsReport } from '../use-stats-report/use-stats-report'
import { mapActions, mapGetters } from '../utils'

interface KpiConfig {
  value: string
  label: string
  color?: ColorEnum
  icon?: string
  iconColor?: ColorEnum
}

type KpiConfigs = Record<KpiEnum, KpiConfig>

export default function useExplainKpis(
  productQuery?: string,
  productId?: string,
): {
  isAllDataLoaded: ComputedRef<boolean>
  kpiConfigs: ComputedRef<KpiConfigs>
  getVisibleAndSortedKpiConfigs: (selectedKpis: KpiEnum[]) => Partial<KpiConfigs>
} {
  const {
    proxy: { $store, $epb },
  } = getCurrentInstance() as { proxy: Vue }

  const { supportedKpis, getKpiLabel, getKpiValue, getKpiStyle } = useKpis() as KpisComposable
  const { query: contextQuery } = useContextQuery()
  useReloadDistDataWhenFiltersChange()

  const { data } = mapGetters($store, ANALYTICS_CONSTS.moduleName, {
    data: ANALYTICS_CONSTS.getters.data,
  })

  const { loadDistData } = mapActions($store, ANALYTICS_CONSTS.moduleName, {
    loadDistData: ANALYTICS_CONSTS.actions.loadDistData,
  })

  const metricsData = computed(() => {
    return data.value.ids[contextQuery.value]?.dist?.currentPeriod?.data
  })

  const reportIds = [
    StatsReportsIdsEnum.RELATED,
    StatsReportsIdsEnum.RELATED_ADD2CART,
    StatsReportsIdsEnum.RELATED_CLICK,
  ]

  const reports = computed(() => {
    return reportIds.map(id =>
      useStatsReport({ statsReportId: id, query: productQuery ?? '', productId: productId ?? '' }),
    )
  })

  const kpiProductMetricMap = computed((): Record<string, number> => {
    return {
      [KpiEnum.CLICKS]: reports.value[2].data.value?.relatedCount ?? 0,
      [KpiEnum.ADD2CART]: reports.value[1].data.value?.relatedCount ?? 0,
      [KpiEnum.POSITION_AVG]:
        reports.value[0].data.value && 'positionAvg' in reports.value[0].data.value
          ? Number(reports.value[0].data.value.positionAvg.toFixed(1))
          : 0,
    }
  })

  const isAllDataLoaded = computed(() => {
    if (productId) {
      return !reports.value.some(report => report.isLoading.value)
    } else {
      return metricsData.value !== undefined
    }
  })

  const kpiConfigs = computed(() => {
    let kpis = supportedKpis
    if (productId) {
      kpis = Object.keys(kpiProductMetricMap.value) as KpiEnum[]
    }
    return kpis
      .map(kpi => (kpi === KpiEnum.FINDABILITY ? KpiEnum.CONVERSION : kpi))
      .reduce((kpis, kpi) => {
        kpis[kpi] = {
          label: capitalizeAllWords(getKpiLabel(kpi)),
          value: getKpiValueByType(kpi),
          icon: getKpiStyle(kpi).icon,
          color: getKpiStyle(kpi).bgColor,
          iconColor: getKpiStyle(kpi).color,
        }
        return kpis
      }, {} as KpiConfigs)
  })

  function getKpiValueByType(kpi: KpiEnum): string {
    return productId ? getProductKpiValue(kpi) : getQueryKpiValue(kpi)
  }

  function getQueryKpiValue(kpi: KpiEnum): string {
    const value = getKpiValue({ kpi, data: metricsData.value }).toLocaleString($epb.locale)
    return kpi === KpiEnum.CONVERSION ? `${value}%` : value
  }

  function getProductKpiValue(kpi: KpiEnum): string {
    const value = kpiProductMetricMap.value[kpi]
    return typeof value === 'number' ? value.toLocaleString($epb.locale) : '0'
  }

  const getVisibleAndSortedKpiConfigs = (selectedKpis: KpiEnum[]): Partial<KpiConfigs> => {
    return (Object.keys(kpiConfigs.value) as Array<KpiEnum>)
      .filter(kpi => selectedKpis.includes(kpi))
      .sort((kpi1, kpi2) => selectedKpis.indexOf(kpi1) - selectedKpis.indexOf(kpi2))
      .reduce((acc, kpi) => {
        acc[kpi] = kpiConfigs.value[kpi]
        return acc
      }, {} as Partial<KpiConfigs>)
  }

  watch(
    () => contextQuery.value,
    async (newQuery: string): Promise<void> => {
      await loadDistData(newQuery)
    },
    { immediate: true },
  )

  return { isAllDataLoaded, kpiConfigs, getVisibleAndSortedKpiConfigs }
}
